import { createFileRoute } from "@tanstack/react-router";

import { LegalPage, LegalSection, CONTACT_EMAIL } from "@/components/legal-page";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
  head: () => ({
    meta: [
      { title: "Privacy Policy | Civil Campus Ambassador Program" },
      {
        name: "description",
        content:
          "How the Civil Engineering Campus Ambassador Program collects, uses, stores and deletes applicant data such as name, college, email and WhatsApp number.",
      },
      { property: "og:title", content: "Privacy Policy | Campus Ambassador Program" },
      {
        property: "og:description",
        content:
          "Learn what applicant data we collect, how it is used for program selection, and how to request deletion.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
});

function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy Policy"
      updated="4 August 2026"
      intro="This page explains what information we collect through the Campus Ambassador application, why we collect it, and the choices you have. It is maintained by the program team."
    >
      <LegalSection heading="Information we collect">
        <ul className="list-disc space-y-2 pl-5">
          <li>Full name</li>
          <li>College name and engineering branch</li>
          <li>Year of study</li>
          <li>WhatsApp number</li>
          <li>Email address</li>
        </ul>
        <p>
          We do not ask for payment card details, government ID numbers or academic
          documents on this website.
        </p>
      </LegalSection>

      <LegalSection heading="How we use it">
        <p>
          Your details are used only to review your application, contact you about
          selection and training, share program updates, and calculate commission and
          rewards if you are onboarded. We do not sell your data or use it for unrelated
          advertising.
        </p>
      </LegalSection>

      <LegalSection heading="Storage and access">
        <p>
          Applications are stored in our managed cloud database with access restricted to
          the program administrator account. Access to the admin dashboard requires a
          verified sign-in.
        </p>
      </LegalSection>

      <LegalSection heading="Retention and deletion">
        <p>
          Applications are kept while your candidature or ambassadorship is active, and
          removed on request. To have your data deleted, email{" "}
          <a
            className="font-semibold text-primary hover:underline"
            href={`mailto:${CONTACT_EMAIL}`}
          >
            {CONTACT_EMAIL}
          </a>{" "}
          from the address you applied with.
        </p>
      </LegalSection>

      <LegalSection heading="Cookies">
        <p>
          This landing page does not use advertising or tracking cookies. Only technical
          storage required to keep the admin session signed in is used.
        </p>
      </LegalSection>

      <LegalSection heading="Your choices">
        <p>
          You can ask us to correct your details, stop contacting you, or delete your
          application at any time. We respond to such requests over email, usually within
          a few working days.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
